import { createContext, useMemo, type ReactNode } from "react";

interface InboxEmbedValue {
  /**
   * z-index class for the layers a composer portals to <body> (template picker,
   * insert-variable menu, template list). They carry their own low defaults,
   * which lose to a floating host painted above the page.
   */
  menuZClass: string;
  /** z-index for modals the composer opens (the email-template dialog). */
  modalZIndex: number;
  /** Lay the composer out for a narrow host (the 400px pop-up card). */
  compact: boolean;
  /** Which pop-up layout hosts the embed — a few compact styles differ. */
  layout?: "floating" | "drawer";
  /**
   * The pop-up's participants block under the chips. When set, the composer
   * portals its From/To routing selects here instead of its own footer.
   */
  participantsEl?: HTMLElement | null;
}

export const InboxEmbedContext = createContext<InboxEmbedValue | undefined>(
  undefined
);

/**
 * Marks an inbox conversation as rendered inside a floating host — the CRM-1133
 * message pop-up — so its portalled layers can clear the host's stacking level
 * and its composer can lay out for a narrow card.
 *
 * Absent on the /inbox route, where every layer keeps its own defaults.
 */
export const InboxEmbedProvider = ({
  menuZClass,
  modalZIndex,
  compact = false,
  layout,
  participantsEl,
  children
}: InboxEmbedValue & { compact?: boolean; children: ReactNode }) => {
  const value = useMemo(
    () => ({ menuZClass, modalZIndex, compact, layout, participantsEl }),
    [menuZClass, modalZIndex, compact, layout, participantsEl]
  );

  return (
    <InboxEmbedContext.Provider value={value}>
      {children}
    </InboxEmbedContext.Provider>
  );
};
